import { Roles } from "../../constants/roles";
import { FormBase } from "../../util/form-base-form";

class SnsPreviewManagerForm extends FormBase {
    public hasPostCreateForm(root: ParentNode = document): boolean {
        return this.queryElement(root, `[data-role="${Roles.POST_CREATE_FORM}"]`, HTMLElement) instanceof HTMLElement;
    }

    public getWysiwygEditor(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.WYSIWYG_EDITOR}"]`, HTMLElement);
    }

    public getWysiwygContent(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.WYSIWYG_CONTENT}"]`, HTMLElement);
    }

    public getPostContent(root: ParentNode = document): HTMLTextAreaElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_CONTENT}"]`, HTMLTextAreaElement);
    }

    public getPostContentTextarea(element: Element | null | undefined): HTMLTextAreaElement | null {
        const form = this.closest(element, "form", HTMLFormElement);
        if (!form) return null;
        return this.queryElement(form, `[data-role="${Roles.POST_CONTENT}"]`, HTMLTextAreaElement);
    }

    public getSnsLinksList(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_LINKS_LIST}"]`, HTMLElement);
    }

    public getSnsLinkInputs(root: ParentNode): HTMLInputElement[] {
        return this.queryElements(root, `[data-role="${Roles.SNS_LINK_INPUT}"]`, HTMLInputElement);
    }

    public getImageElements(root: ParentNode): HTMLImageElement[] {
        return this.queryElements(root, "img", HTMLImageElement);
    }

    public getBoardMediaArea(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.BOARD_MEDIA_AREA}"]`, HTMLElement);
    }

    public getClosestPreviewTile(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.SNS_PREVIEW_TILE}"]`, HTMLElement);
    }

    public getClosestBoardMediaArea(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.BOARD_MEDIA_AREA}"]`, HTMLElement);
    }

    public getClosestPostDetail(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.POST_DETAIL}"]`, HTMLElement);
    }

    public getClosestBoardImagesGrid(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.BOARD_IMAGES_GRID}"]`, HTMLElement);
    }

    public getBoardImages(root: ParentNode): HTMLImageElement[] {
        return this.queryElements(root, "img.board-image", HTMLImageElement);
    }

    public getPostElements(root: ParentNode, selector: string): HTMLElement[] {
        return this.queryElements(root, selector, HTMLElement);
    }

    public getHiddenContent(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".hidden", HTMLElement);
    }

    public getDisplayContent(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.POST_CONTENT_DISPLAY}"], [data-role="${Roles.POST_DETAIL_CONTENT}"]`,
            HTMLElement,
        );
    }

    public getSnsContent(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_CONTENT}"]`, HTMLElement);
    }

    public getSnsLinksDataElements(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.POST_SNS_LINKS_DATA}"]`, HTMLElement);
    }

    public hasAttachedCardMedia(root: ParentNode): boolean {
        return (
            this.queryElement(
                root,
                `[data-role="${Roles.BOARD_IMAGES_GRID}"], [data-role="${Roles.BOARD_VIDEO_WRAP}"], [data-role="${Roles.SNS_PREVIEW_TILES_GRID}"]`,
                HTMLElement,
            ) instanceof HTMLElement
        );
    }

    public getBoardMediaPlaceholder(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.BOARD_MEDIA_PLACEHOLDER}"]`, HTMLElement);
    }

    public getBoardImagesInMediaArea(root: ParentNode): HTMLImageElement[] {
        return this.queryElements(root, `[data-role="${Roles.BOARD_IMAGES_GRID}"] img.board-image`, HTMLImageElement);
    }

    public getBoardVideoWrap(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.BOARD_VIDEO_WRAP}"]`, HTMLElement);
    }

    public getSnsPreviewTilesGrid(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_PREVIEW_TILES_GRID}"]`, HTMLElement);
    }

    public getPostTitle(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.POST_CARD_TITLE}"], [data-role="${Roles.POST_DETAIL_TITLE}"]`,
            HTMLElement,
        );
    }

    public getContentElements(root: ParentNode, selector: string): HTMLElement[] {
        return this.queryElements(root, selector, HTMLElement);
    }

    public isInsideModal(root: Event | EventTarget | Element | null | undefined): boolean {
        return (
            this.closest(
                root,
                `[data-role="${Roles.ELEMENT_MODAL_OVERLAY}"], [data-role="${Roles.ELEMENT_MODAL}"]`,
                HTMLElement,
            ) instanceof HTMLElement
        );
    }

    public isInsidePostCard(root: Event | EventTarget | Element | null | undefined): boolean {
        return this.closest(root, `[data-role="${Roles.POST_CARD}"]`, HTMLElement) instanceof HTMLElement;
    }

    public isInsidePreviewBody(root: Event | EventTarget | Element | null | undefined): boolean {
        return this.closest(root, ".preview-body", HTMLElement) instanceof HTMLElement;
    }

    public getSnsEmbeds(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.SNS_EMBED}"]`, HTMLElement);
    }

    public getYoutubePreviews(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.YOUTUBE_PREVIEW}"]`, HTMLElement);
    }

    public getOgpPreviews(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.OGP_PREVIEW_CARD}"], .ogp-preview`, HTMLElement);
    }

    public queryScopedElement(root: ParentNode, selector: string): HTMLElement | null {
        return this.queryElement(root, selector, HTMLElement);
    }

    public getPlaceholderLoadingText(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".placeholder-text.loading-placeholder", HTMLElement);
    }

    public getPlaceholderIcon(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".placeholder-icon", HTMLElement);
    }

    public getOgpPreviewCard(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.OGP_PREVIEW_CARD}"]`, HTMLElement);
    }

    public getAnchorElementsByHref(root: ParentNode, url: string): HTMLAnchorElement[] {
        return this.queryElements(root, `a[href="${url}"]`, HTMLAnchorElement);
    }

    public findCardMediaArea(root: ParentNode): HTMLElement | null {
        return (
            this.getBoardMediaArea(root) ||
            this.queryElement(root, ".content-wrapper", HTMLElement) ||
            this.queryElement(root, ".card-body", HTMLElement) ||
            this.getDisplayContent(root) ||
            this.queryElement(root, ".board-content", HTMLElement) ||
            this.queryElement(root, ".placeholder-icon", HTMLElement)
        );
    }

    public getAlternativeCardMediaArea(root: ParentNode, selector: string): HTMLElement | null {
        return this.queryElement(root, selector, HTMLElement);
    }

    public getClosestPostCard(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.POST_CARD}"]`, HTMLElement);
    }

    public getSnsOpenElements(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.SNS_OPEN}"]`, HTMLElement);
    }

    public getSnsOpenAnchor(root: ParentNode): HTMLAnchorElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_OPEN}"]`, HTMLAnchorElement);
    }

    public getMediaControls(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.MEDIA_CONTROLS}"]`, HTMLElement);
    }

    public getSnsPreviewThumb(root: ParentNode): HTMLImageElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_PREVIEW_THUMB}"]`, HTMLImageElement);
    }

    public getExistingCardPreview(root: ParentNode, role: string): HTMLElement | null {
        return this.queryElement(root, `[data-role="${role}"]`, HTMLElement);
    }

    public getExistingPrimaryPreview(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.SNS_PREVIEW_TILE}"], [data-role="${Roles.OGP_PREVIEW_CARD}"], [data-role="${Roles.YOUTUBE_PREVIEW}"], [data-role="${Roles.FACEBOOK_PREVIEW}"]`,
            HTMLElement,
        );
    }

    public getExistingIcons(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".sns-icons", HTMLElement);
    }

    public getBoardActions(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".board-actions", HTMLElement);
    }

    public getBoardFooterSns(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".board-footer-sns", HTMLElement);
    }

    public getBoardFooterSnsList(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, ".board-footer-sns", HTMLElement);
    }

    public getPlatformBadges(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, ".sns-preview-tile__platform-badge", HTMLElement);
    }

    public getPlainPlaceholderIcon(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".placeholder-icon", HTMLElement);
    }

    public getPlainPlaceholderText(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".placeholder-text", HTMLElement);
    }

    public getTileUrlElements(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.SNS_PREVIEW_TILE}"][data-sns-url]`, HTMLElement);
    }

    public getSnsOpenHrefAnchors(root: ParentNode): HTMLAnchorElement[] {
        return this.queryElements(root, `[data-role="${Roles.SNS_OPEN}"][href]`, HTMLAnchorElement);
    }

    public hasElement(root: ParentNode, selector: string): boolean {
        return this.queryElement(root, selector, HTMLElement) instanceof HTMLElement;
    }

    public getAdSlots(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, "[data-ad-slot]", HTMLElement);
    }
}

const snsPreviewManagerForm = new SnsPreviewManagerForm();

export { SnsPreviewManagerForm, snsPreviewManagerForm };
